import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * /assessments/:id — full assessment editor as a page (was a drawer).
 * Header carries breadcrumbs + a delete + (for passing kinds linked to a
 * lesson) an "נתק" (unlink) action + a back-link to the lesson.
 *
 * The editor body itself is the same component the drawer uses; we only
 * change the container from Drawer → page.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Alert, Button, Card, Popconfirm, Space, Spin, Tag } from 'antd';
import { ArrowRightOutlined, DeleteOutlined, DisconnectOutlined } from '@ant-design/icons';
import { deleteAssessment, getAssessment, getContentTree, updateAssessmentLessonLink, } from '../../api';
import { PageHeader } from '../../components/PageHeader';
import { notifySuccess } from '../../components/errors';
import { ContentTreeProvider } from '../../components/entityPickers';
import { AssessmentEditorPageBody, AssessmentLinkageSummary, } from '../../components/assessments/AssessmentEditorDrawer';
export default function AssessmentPageScreen() {
    const nav = useNavigate();
    const { id: idParam } = useParams();
    const id = Number(idParam);
    const [detail, setDetail] = useState(undefined);
    // Reload trigger — used to refetch after unlink so header info stays fresh.
    // The editor body has its own internal fetch/reload; this state is only for
    // the header info we render around it.
    const [reloadKey, setReloadKey] = useState(0);
    const load = useCallback(async () => {
        try {
            setDetail(await getAssessment(id));
        }
        catch {
            setDetail(null); /* client auto-surfaces */
        }
    }, [id]);
    useEffect(() => { void load(); }, [load, reloadKey]);
    if (detail === undefined)
        return _jsx(Spin, {});
    if (!detail) {
        return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: "\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0", breadcrumb: [{ label: 'ניהול' }, { label: 'בחנים', to: '/assessments' }, 'לא נמצא'] }), _jsx(Alert, { type: "warning", message: "\u05D4\u05D1\u05D5\u05D7\u05DF \u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0", description: _jsx(_Fragment, { children: _jsx(Link, { to: "/assessments", children: "\u05D7\u05D6\u05D5\u05E8 \u05DC\u05E8\u05E9\u05D9\u05DE\u05EA \u05D4\u05D1\u05D7\u05E0\u05D9\u05DD" }) }) })] }));
    }
    const del = async () => {
        try {
            await deleteAssessment(id);
            notifySuccess('בוחן נמחק');
            nav('/assessments');
        }
        catch { /* client auto-surfaces */ }
    };
    const unlink = async () => {
        try {
            await updateAssessmentLessonLink(id, null);
            notifySuccess('הבוחן נותק מהשיעור');
            setReloadKey((k) => k + 1);
        }
        catch { /* client auto-surfaces */ }
    };
    return (_jsxs(ContentTreeProvider, { children: [_jsx(PageHeader, { title: detail.name ?? `בוחן #${detail.id}`, breadcrumb: [
                    { label: 'ניהול' },
                    { label: 'בחנים', to: '/assessments' },
                    detail.name ?? `#${detail.id}`,
                ], actions: _jsxs(Space, { children: [detail.kind === 'passing' && detail.lesson_id != null && (_jsx(Popconfirm, { title: "\u05DC\u05E0\u05EA\u05E7 \u05D0\u05EA \u05D4\u05D1\u05D5\u05D7\u05DF \u05DE\u05D4\u05E9\u05D9\u05E2\u05D5\u05E8?", description: "\u05D4\u05D1\u05D5\u05D7\u05DF \u05D9\u05E9\u05E8\u05D5\u05D3 \u05D5\u05D0\u05E4\u05E9\u05E8 \u05D9\u05D4\u05D9\u05D4 \u05DC\u05D4\u05E6\u05DE\u05D9\u05D3 \u05D0\u05D5\u05EA\u05D5 \u05DC\u05E9\u05D9\u05E2\u05D5\u05E8 \u05D0\u05D7\u05E8.", okText: "\u05E0\u05EA\u05E7", cancelText: "\u05D1\u05D8\u05DC", onConfirm: unlink, children: _jsx(Button, { icon: _jsx(DisconnectOutlined, {}), children: "\u05E0\u05EA\u05E7" }) })), _jsx(Popconfirm, { title: "\u05DC\u05DE\u05D7\u05D5\u05E7 \u05D0\u05EA \u05D4\u05D1\u05D5\u05D7\u05DF?", okText: "\u05DE\u05D7\u05E7", cancelText: "\u05D1\u05D8\u05DC", onConfirm: del, children: _jsx(Button, { danger: true, icon: _jsx(DeleteOutlined, {}), children: "\u05DE\u05D7\u05E7" }) })] }) }), _jsxs(Space, { direction: "vertical", size: 16, style: { width: '100%' }, children: [_jsx(Card, { size: "small", children: _jsxs(Space, { wrap: true, children: [_jsx(Tag, { color: detail.kind === 'blocking' ? 'red' : detail.kind === 'passing' ? 'blue' : undefined, children: detail.kind }), _jsx(AssessmentLinkageSummary, { assessment: detail }), detail.kind === 'passing' && detail.lesson_id != null && (_jsx(BackToLessonLink, { lessonId: detail.lesson_id }))] }) }), _jsx(AssessmentEditorPageBody, { assessmentId: id, onSaved: () => { setReloadKey((k) => k + 1); }, onClose: () => nav('/assessments') })] })] }));
}
/**
 * Resolves the current lesson_id to a full breadcrumb path so we can offer
 * "חזור לעמוד השיעור" as a real link (not just the resolved label).
 */
function BackToLessonLink({ lessonId }) {
    const [path, setPath] = useState(null);
    useEffect(() => {
        // Walk the content tree to find where this lesson lives so the back-link
        // targets the full lesson URL (grade + track + lesson). The tree fetch
        // is small and shared by ContentTreeProvider — the cost is minor.
        (async () => {
            try {
                const tree = await getContentTree();
                for (const g of tree.grades)
                    for (const t of g.tracks)
                        for (const l of t.lessons) {
                            if (l.id === lessonId) {
                                setPath({ gradeId: g.id, trackId: t.id });
                                return;
                            }
                        }
            }
            catch { /* silently skip — the link is just a convenience */ }
        })();
    }, [lessonId]);
    if (!path)
        return null;
    return (_jsx(Link, { to: `/grades/${path.gradeId}/tracks/${path.trackId}/lessons/${lessonId}`, children: _jsx(Button, { size: "small", icon: _jsx(ArrowRightOutlined, {}), children: "\u05D7\u05D6\u05D5\u05E8 \u05DC\u05E2\u05DE\u05D5\u05D3 \u05D4\u05E9\u05D9\u05E2\u05D5\u05E8" }) }));
}
